import { Injectable, signal, inject } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { CSVHeaderMappingInterface, CRMCustomObjectTypeApiService, CustomObjectTypeInterface } from '@vendasta/crm';
import { Observable, of, take } from 'rxjs';
import { CrmDependencies, CrmInjectionToken } from '@galaxy/crm/static';

export type Step = 'fileUploadCompleted' | 'fieldMappingCompleted';
export type PermissionType =
  | 'contactPermissions'
  | 'companyPermissions'
  | 'customObjectPermissions'
  | 'opportunitiesPermissions';
export type CsvRow = { [key: string]: CsvRowValue };
export interface CsvRowValue {
  value: string;
  isDuplicateHeaderName: boolean;
}

@Injectable()
export class BulkImportStepperService {
  private readonly csvFilenameSignal = signal<string>('');
  private readonly csvRowsSignal = signal<string>('0');
  private readonly csvIsRowEstimateSignal = signal<boolean>(false);
  private readonly csvFirstRowSignal = signal<CsvRow>({});
  private readonly csvHeaderMappingsSignal = signal<CSVHeaderMappingInterface[]>([]);
  private readonly fileUploadCompletedSignal = signal<boolean>(false);
  private readonly fieldMappingCompletedSignal = signal<boolean>(false);
  private readonly contactPermissionsSignal = signal<boolean>(false);
  private readonly companyPermissionsSignal = signal<boolean>(false);
  private readonly customObjectPermissionsSignal = signal<boolean>(false);
  private readonly customObjectTypesSignal = signal<CustomObjectTypeInterface[]>([]);
  private readonly opportunitiesPermissionsSignal = signal<boolean>(false);

  csvFilename$: Observable<string> = toObservable(this.csvFilenameSignal);
  csvRows$: Observable<string> = toObservable(this.csvRowsSignal);
  csvIsRowEstimate$: Observable<boolean> = toObservable(this.csvIsRowEstimateSignal);
  csvFirstRow$: Observable<CsvRow> = toObservable(this.csvFirstRowSignal);
  csvMappings$: Observable<CSVHeaderMappingInterface[]> = toObservable(this.csvHeaderMappingsSignal);
  fileUploadCompleted$: Observable<boolean> = toObservable(this.fileUploadCompletedSignal);
  fieldMappingCompleted$: Observable<boolean> = toObservable(this.fieldMappingCompletedSignal);
  contactPermissions$: Observable<boolean> = toObservable(this.contactPermissionsSignal);
  companyPermissions$: Observable<boolean> = toObservable(this.companyPermissionsSignal);
  customObjectPermissions$: Observable<boolean> = toObservable(this.customObjectPermissionsSignal);
  customObjectTypes$: Observable<CustomObjectTypeInterface[]> = toObservable(this.customObjectTypesSignal);
  opportunitiesPermissions$: Observable<boolean> = toObservable(this.opportunitiesPermissionsSignal);

  private readonly config: CrmDependencies = inject(CrmInjectionToken);
  private readonly customObjectTypeService = inject(CRMCustomObjectTypeApiService);

  constructor() {
    this.reset();
    this.loadCustomObjectTypes();
  }

  reset(): void {
    this.csvFilenameSignal.set('');
    this.csvRowsSignal.set('0');
    this.csvIsRowEstimateSignal.set(false);
    this.csvFirstRowSignal.set({});
    this.csvHeaderMappingsSignal.set([]);
    this.fileUploadCompletedSignal.set(false);
    this.fieldMappingCompletedSignal.set(false);
    this.contactPermissionsSignal.set(false);
    this.companyPermissionsSignal.set(false);
    this.customObjectPermissionsSignal.set(false);
    this.customObjectTypesSignal.set([]);
    this.opportunitiesPermissionsSignal.set(false);
  }

  private loadCustomObjectTypes(): void {
    if (!this.config.customObjectTypes$) {
      this.customObjectTypesSignal.set([]);
      return;
    } else {
      this.config.customObjectTypes$.pipe(take(1)).subscribe({
        next: (customObjectTypes) => {
          this.customObjectTypesSignal.set(customObjectTypes);
        },
        error: () => {
          this.customObjectTypesSignal.set([]);
        },
      });
    }
  }

  isStepComplete(stepKey: Step): boolean {
    switch (stepKey) {
      case 'fileUploadCompleted':
        return this.fileUploadCompletedSignal();
      case 'fieldMappingCompleted':
        return this.fieldMappingCompletedSignal();
      default:
        return false;
    }
  }

  completeStep(stepKey: Step, complete: boolean) {
    switch (stepKey) {
      case 'fileUploadCompleted':
        this.fileUploadCompletedSignal.set(complete);
        break;
      case 'fieldMappingCompleted':
        this.fieldMappingCompletedSignal.set(complete);
        break;
    }
  }

  hasPermission$(permissionType: PermissionType): Observable<boolean> {
    switch (permissionType) {
      case 'contactPermissions':
        return of(this.contactPermissionsSignal());
      case 'companyPermissions':
        return of(this.companyPermissionsSignal());
      case 'customObjectPermissions':
        return of(this.customObjectPermissionsSignal());
      case 'opportunitiesPermissions':
        return of(this.opportunitiesPermissionsSignal());
      default:
        return of(false);
    }
  }

  setPermission(permissionType: PermissionType, complete: boolean) {
    switch (permissionType) {
      case 'contactPermissions':
        this.contactPermissionsSignal.set(complete);
        break;
      case 'companyPermissions':
        this.companyPermissionsSignal.set(complete);
        break;
      case 'customObjectPermissions':
        this.customObjectPermissionsSignal.set(complete);
        break;
      case 'opportunitiesPermissions':
        this.opportunitiesPermissionsSignal.set(complete);
        break;
    }
  }

  setCSVFilename(csvFilename: string) {
    this.csvFilenameSignal.set(csvFilename);
  }

  setCSVRows(csvRows: string, isEstimated: boolean): void {
    this.csvRowsSignal.set(csvRows);
    this.csvIsRowEstimateSignal.set(isEstimated);
  }

  setCSVFirstRow(csvFirstRow: CsvRow): void {
    this.csvFirstRowSignal.set(csvFirstRow);
  }

  setCSVHeaderMappings(csvHeaderMappings: CSVHeaderMappingInterface[]): void {
    this.csvHeaderMappingsSignal.set(csvHeaderMappings);
  }
}
